/**
 * Convert a USD amount to a locale-appropriate currency for display.
 *
 * SCOPE: Company-wide USD stats only (e.g. "$7b+ deployed").
 * Do NOT use for product prices, financing amounts, or eligibility
 * thresholds — those use CurrencyMark or locale message overrides
 * which reflect actual local pricing, not FX conversions.
 *
 * Uses static approximate exchange rates — NOT live rates.
 * Compact suffix is always lowercase `b`/`m`/`k` — never `bn`.
 * Update rates periodically (quarterly) to keep them roughly accurate.
 *
 * Rounding defaults to nearest (e.g. "$7b" → "€6b"), which suits an exact
 * historical figure such as a fundraising round. Pass "down" for a stat that
 * copy presents as a minimum with a trailing `+`: £5.53b must read "£5b+", not
 * "£6b+", which would claim more capital than has been deployed.
 */

import { LOCALE_CURRENCY } from "./locale-prefix";

/** Last FX rate review date (quarterly cadence). */
export const USD_RATES_REVIEWED_AT = "2026-08-21";

/** Approximate USD → target currency rates. Reviewed {@link USD_RATES_REVIEWED_AT}. */
const USD_RATES: Record<string, number> = {
  USD: 1,
  GBP: 0.79,
  EUR: 0.91,
  AUD: 1.57,
};

/** Convert a USD figure into the currency assigned to a site locale. */
export function convertUsdAmount(usdAmount: number, locale: string): number {
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  return Math.round(usdAmount * (USD_RATES[currency] ?? 1));
}

/**
 * How to resolve a figure onto its compact unit. "nearest" for exact amounts,
 * "down" for a minimum the copy suffixes with `+`.
 */
export type CompactRounding = "nearest" | "down";

/** Truncate to the compact unit the formatter will use: 5,530,000,000 → 5,000,000,000. */
function floorToCompactUnit(value: number): number {
  const unit = [1e9, 1e6, 1e3].find((candidate) => value >= candidate) ?? 1;
  return Math.floor(value / unit) * unit;
}

/**
 * Convert a USD amount to a locale currency and format as compact string.
 *
 * @param usdAmount - Amount in USD (e.g. 7_000_000_000)
 * @param locale - Locale code (e.g. "en-GB", "en-IE", "de")
 * @param rounding - "nearest" (default) for exact figures, "down" for `+` minimums
 * @returns Locale-formatted compact currency amount.
 */
export function convertedAmount(
  usdAmount: number,
  locale: string,
  rounding: CompactRounding = "nearest"
): string {
  const currency = LOCALE_CURRENCY[locale] ?? "USD";
  const exact = convertUsdAmount(usdAmount, locale);
  const converted = rounding === "down" ? floorToCompactUnit(exact) : exact;

  // Use "en-AU" for AUD so it renders "$" not "A$"; "en" for all others
  // to keep compact abbreviations in Latin form (b/m/k) e.g. "€5b" not "5 Mrd. €".
  const formatLocale = currency === "AUD" ? "en-AU" : "en";
  const parts = new Intl.NumberFormat(formatLocale, {
    style: "currency",
    currency,
    notation: "compact",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).formatToParts(converted);

  const placement = locale === "de" || locale === "es" ? "after" : "before";

  if (placement === "after") {
    const currencyPart = parts.find((p) => p.type === "currency")?.value ?? "";
    const numberParts = parts
      .filter((p) => p.type !== "currency")
      .map((part) =>
        part.type === "compact" ? part.value.toLowerCase() : part.value
      )
      .join("")
      .trim();
    return `${numberParts} ${currencyPart}`;
  }

  return parts
    .map((part) =>
      part.type === "compact" ? part.value.toLowerCase() : part.value
    )
    .join("");
}
